import React, { useState, useEffect } from "react";
import { analyticsAPI, listAPI, categoryAPI, itemAPI } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";

const AdminDashboard = () => {
  usePageTitle("Admin Dashboard");
  const [activeTab, setActiveTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [lists, setLists] = useState([]);
  const [searchedLists, setSearchedLists] = useState([]);
  const [searchedListsForManagement, setSearchedListsForManagement] = useState(
    []
  );
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [listItems, setListItems] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [editingList, setEditingList] = useState(null);
  const [showFormattingHelp, setShowFormattingHelp] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchingLists, setIsSearchingLists] = useState(false);
  const [listSearchQuery, setListSearchQuery] = useState("");
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [itemPage, setItemPage] = useState(0);

  const [listForm, setListForm] = useState({
    title: "",
    description: "",
    isRanked: false,
    isEnabled: true,
    isPrivate: false,
    categoryIds: [],
  });

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
  });

  const [itemForm, setItemForm] = useState({
    answer: "",
    rankPosition: null,
  });

  useEffect(() => {
    loadAnalytics();
    loadLists();
    loadCategories();
    loadUsers();
  }, []);

  useEffect(() => {
    loadLists();
  }, [currentPage]);

  useEffect(() => {
    // Debounce the search for lists management tab
    const timeoutId = setTimeout(() => {
      searchListsForManagement(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    // Debounce the search for items tab
    const timeoutId = setTimeout(() => {
      searchListsForItems(listSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [listSearchQuery]);

  const loadAnalytics = async () => {
    try {
      const response = await analyticsAPI.getOverallAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to load analytics:", error);
    }
  };

  const loadLists = async () => {
    try {
      const response = await listAPI.getAllListsAdmin(currentPage, pageSize);
      setLists(response.data.content);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Failed to load lists:", error);
    }
  };

  const searchListsForManagement = async (query) => {
    if (!query.trim()) {
      setSearchedListsForManagement([]);
      setIsSearchingLists(false);
      return;
    }

    setIsSearchingLists(true);
    try {
      // Use the new efficient backend search endpoint
      const response = await listAPI.searchListsAdmin(query, 0, 100);
      setSearchedListsForManagement(response.data.content);
    } catch (error) {
      console.error("Failed to search lists:", error);
      setSearchedListsForManagement([]);
    }
  };

  const searchListsForItems = async (query) => {
    if (!query.trim()) {
      setSearchedLists([]);
      return;
    }

    try {
      // Use the new efficient backend search endpoint
      const response = await listAPI.searchListsAdmin(query, 0, 100);
      setSearchedLists(response.data.content);
    } catch (error) {
      console.error("Failed to search lists:", error);
      setSearchedLists([]);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoryAPI.getAllCategories();
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await analyticsAPI.getAllUsers();
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  };

  const loadListItems = async (listId) => {
    try {
      const response = await itemAPI.getItemsByListId(listId);
      setListItems(response.data);
    } catch (error) {
      console.error("Failed to load items:", error);
    }
  };

  const handleCreateList = async (e) => {
    e.preventDefault();
    try {
      if (editingList) {
        await listAPI.updateList(editingList, listForm);
        showMessage("List updated successfully!", "success");
        setEditingList(null);
      } else {
        await listAPI.createList(listForm);
        showMessage("List created successfully!", "success");
      }
      await loadLists();
      // Refresh search results if there's an active search in either tab
      if (searchQuery.trim()) {
        await searchListsForManagement(searchQuery);
      }
      if (listSearchQuery.trim()) {
        await searchListsForItems(listSearchQuery);
      }
      resetListForm();
    } catch (error) {
      showMessage("Failed to save list", "error");
    }
  };

  const handleEditList = (list) => {
    setEditingList(list.id);
    setListForm({
      title: list.title,
      description: list.description || "",
      isRanked: list.isRanked,
      isEnabled: list.isEnabled,
      isPrivate: list.isPrivate,
      categoryIds: list.categories.map((cat) => cat.id),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingList(null);
    resetListForm();
  };

  const resetListForm = () => {
    setListForm({
      title: "",
      description: "",
      isRanked: false,
      isEnabled: true,
      isPrivate: false,
      categoryIds: [],
    });
  };

  const handleDeleteList = async (listId) => {
    if (
      window.confirm(
        "Are you sure? This will delete the list and all progress!"
      )
    ) {
      try {
        await listAPI.deleteList(listId);
        showMessage("List deleted successfully!", "success");
        await loadLists();
        // Refresh search results if there's an active search in either tab
        if (searchQuery.trim()) {
          await searchListsForManagement(searchQuery);
        }
        if (listSearchQuery.trim()) {
          await searchListsForItems(listSearchQuery);
        }
        setSelectedList(null);
      } catch (error) {
        showMessage("Failed to delete list", "error");
      }
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await categoryAPI.updateCategory(editingCategory, categoryForm);
        showMessage("Category updated successfully!", "success");
        setEditingCategory(null);
      } else {
        await categoryAPI.createCategory(categoryForm);
        showMessage("Category created successfully!", "success");
      }
      await loadCategories();
      setCategoryForm({ name: "", description: "" });
    } catch (error) {
      showMessage("Failed to save category", "error");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await categoryAPI.deleteCategory(categoryId);
        showMessage("Category deleted successfully!", "success");
        await loadCategories();
      } catch (error) {
        showMessage("Failed to delete category", "error");
      }
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category.id);
    setCategoryForm({
      name: category.name,
      description: category.description || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEditCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ name: "", description: "" });
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!selectedList) return;

    try {
      await itemAPI.addItemToList(selectedList, itemForm);
      showMessage("Item added successfully!", "success");
      await loadListItems(selectedList);
      await loadLists();
      // Refresh search results if there's an active search
      if (listSearchQuery.trim()) {
        await searchListsForItems(listSearchQuery);
      }
      setItemForm({ answer: "", rankPosition: null });
    } catch (error) {
      showMessage(
        error.response?.data?.message || "Failed to add item",
        "error"
      );
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await itemAPI.deleteItem(itemId);
      showMessage("Item deleted successfully!", "success");
      await loadListItems(selectedList);
      await loadLists();
      // Refresh search results if there's an active search
      if (listSearchQuery.trim()) {
        await searchListsForItems(listSearchQuery);
      }
    } catch (error) {
      showMessage("Failed to delete item", "error");
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item.id);
    setItemForm({
      answer: item.answer,
      rankPosition: item.rankPosition || null,
    });
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (!editingItem) return;

    try {
      await itemAPI.updateItem(editingItem, itemForm);
      showMessage("Item updated successfully!", "success");
      await loadListItems(selectedList);
      await loadLists();
      // Refresh search results if there's an active search
      if (listSearchQuery.trim()) {
        await searchListsForItems(listSearchQuery);
      }
      setEditingItem(null);
      setItemForm({ answer: "", rankPosition: null });
    } catch (error) {
      showMessage("Failed to update item", "error");
    }
  };

  const handleCancelEditItem = () => {
    setEditingItem(null);
    setItemForm({ answer: "", rankPosition: null });
  };

  const getFilteredUsers = () => {
    if (!userSearchQuery.trim()) return users;

    const query = userSearchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.username.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
    );
  };

  const handleDeleteUser = async (userId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this user? This will delete all their progress!"
      )
    ) {
      try {
        await analyticsAPI.deleteUser(userId);
        showMessage("User deleted successfully!", "success");
        await loadUsers();
        await loadAnalytics();
      } catch (error) {
        showMessage(
          error.response?.data?.message || "Failed to delete user",
          "error"
        );
      }
    }
  };

  const getFilteredItems = () => {
    if (!itemSearchQuery.trim()) return listItems;

    return listItems.filter((item) =>
      item.answer.toLowerCase().includes(itemSearchQuery.toLowerCase())
    );
  };

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 3000);
  };

  const handleCategoryToggle = (categoryId) => {
    setListForm((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter((id) => id !== categoryId)
        : [...prev.categoryIds, categoryId],
    }));
  };

  const handleListSelect = async (listId) => {
    setSelectedList(listId);
    if (listId) {
      await loadListItems(listId);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">
        Admin Dashboard
      </h1>

      {message && (
        <div
          className={`mb-6 px-4 py-3 rounded text-sm sm:text-base ${
            messageType === "success"
              ? "bg-green-500/10 border border-green-500 text-green-500"
              : "bg-red-500/10 border border-red-500 text-red-500"
          }`}
        >
          {message}
        </div>
      )}

      {/* Mobile Tab Selector */}
      <div className="mb-6 sm:mb-8">
        <div className="sm:hidden">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="w-full px-4 py-2 bg-dark-card border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="overview">Overview</option>
            <option value="lists">Manage Lists</option>
            <option value="categories">Manage Categories</option>
            <option value="items">Manage Items</option>
            <option value="users">Manage Users</option>
          </select>
        </div>

        {/* Desktop Tabs */}
        <div className="hidden sm:flex space-x-2 md:space-x-4 border-b border-gray-700 overflow-x-auto">
          <button
            onClick={() => setActiveTab("overview")}
            className={`pb-3 px-3 md:px-4 font-semibold transition whitespace-nowrap text-sm md:text-base ${
              activeTab === "overview"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("lists")}
            className={`pb-3 px-3 md:px-4 font-semibold transition whitespace-nowrap text-sm md:text-base ${
              activeTab === "lists"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Lists
          </button>
          <button
            onClick={() => setActiveTab("categories")}
            className={`pb-3 px-3 md:px-4 font-semibold transition whitespace-nowrap text-sm md:text-base ${
              activeTab === "categories"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Categories
          </button>
          <button
            onClick={() => setActiveTab("items")}
            className={`pb-3 px-3 md:px-4 font-semibold transition whitespace-nowrap text-sm md:text-base ${
              activeTab === "items"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Items
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`pb-3 px-3 md:px-4 font-semibold transition whitespace-nowrap text-sm md:text-base ${
              activeTab === "users"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Users
          </button>
        </div>
      </div>

      {activeTab === "overview" && analytics && (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">
            Statistics
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
              <h3 className="text-gray-400 text-xs sm:text-sm mb-2">
                Total Users
              </h3>
              <p className="text-3xl sm:text-4xl font-bold text-white">
                {analytics.totalUsers}
              </p>
            </div>
            <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
              <h3 className="text-gray-400 text-xs sm:text-sm mb-2">
                Total Lists
              </h3>
              <p className="text-3xl sm:text-4xl font-bold text-blue-400">
                {analytics.totalLists}
              </p>
            </div>
            <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
              <h3 className="text-gray-400 text-xs sm:text-sm mb-2">
                Total Categories
              </h3>
              <p className="text-3xl sm:text-4xl font-bold text-green-400">
                {analytics.totalCategories}
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === "lists" && (
        <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-8">
          <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              {editingList ? "Edit List" : "Create New List"}
            </h2>
            <form onSubmit={handleCreateList} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                  Title *
                </label>
                <input
                  type="text"
                  value={listForm.title}
                  onChange={(e) =>
                    setListForm({ ...listForm, title: e.target.value })
                  }
                  required
                  className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-gray-300 text-sm sm:text-base">
                    Description
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowFormattingHelp(!showFormattingHelp)}
                    className="text-blue-400 hover:text-blue-300 text-xs"
                  >
                    {showFormattingHelp ? "Hide" : "Help"}
                  </button>
                </div>

                {showFormattingHelp && (
                  <div className="bg-dark-bg border border-gray-600 rounded p-3 mb-2 text-xs text-gray-300 space-y-1">
                    <p>
                      <code className="bg-gray-700 px-1 rounded">**bold**</code>{" "}
                      = <strong>bold</strong>
                    </p>
                    <p>
                      <code className="bg-gray-700 px-1 rounded">*italic*</code>{" "}
                      = <em>italic</em>
                    </p>
                    <p>
                      <code className="bg-gray-700 px-1 rounded">- item</code> =
                      Bullet point
                    </p>
                  </div>
                )}

                <textarea
                  value={listForm.description}
                  onChange={(e) =>
                    setListForm({ ...listForm, description: e.target.value })
                  }
                  rows="4"
                  placeholder="Supports **bold**, *italic*, lists"
                  className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                  Categories
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto bg-dark-bg p-3 rounded-lg border border-gray-600">
                  {categories.map((cat) => (
                    <label
                      key={cat.id}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={listForm.categoryIds.includes(cat.id)}
                        onChange={() => handleCategoryToggle(cat.id)}
                        className="rounded"
                      />
                      <span className="text-white text-sm sm:text-base">
                        {cat.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3 bg-dark-bg p-3 sm:p-4 rounded-lg border border-gray-700">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={listForm.isRanked}
                    onChange={(e) =>
                      setListForm({ ...listForm, isRanked: e.target.checked })
                    }
                    className="w-4 h-4 rounded mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="text-white font-medium text-sm sm:text-base">
                      Ranked List
                    </span>
                    <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
                      Items have specific order
                    </p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={listForm.isEnabled}
                    onChange={(e) =>
                      setListForm({ ...listForm, isEnabled: e.target.checked })
                    }
                    className="w-4 h-4 rounded mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="text-white font-medium text-sm sm:text-base">
                      Enabled
                    </span>
                    <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
                      Visible and playable
                    </p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={listForm.isPrivate}
                    onChange={(e) =>
                      setListForm({ ...listForm, isPrivate: e.target.checked })
                    }
                    className="w-4 h-4 rounded mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="text-white font-medium text-sm sm:text-base">
                      Private
                    </span>
                    <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
                      Only you can see this
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition text-sm sm:text-base"
                >
                  {editingList ? "Update" : "Create"}
                </button>
                {editingList && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 sm:px-6 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg transition text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
            <div className="mb-4">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                All Lists
              </h2>
              <input
                type="text"
                placeholder="Search lists..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm sm:text-base focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="space-y-3 max-h-[400px] sm:max-h-[600px] overflow-y-auto">
              {(isSearchingLists ? searchedListsForManagement : lists).map(
                (list) => (
                  <div
                    key={list.id}
                    className="bg-dark-bg border border-gray-600 p-3 sm:p-4 rounded-lg"
                  >
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <h3 className="text-white font-semibold text-sm sm:text-base line-clamp-2">
                        {list.title}
                      </h3>
                      <div className="flex flex-wrap gap-1 sm:gap-2 flex-shrink-0">
                        {!list.isEnabled && (
                          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-600/20 text-red-400 text-xs rounded whitespace-nowrap">
                            Disabled
                          </span>
                        )}
                        {list.isPrivate && (
                          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded whitespace-nowrap">
                            Private
                          </span>
                        )}
                        {list.isRanked && (
                          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-600/20 text-blue-400 text-xs rounded whitespace-nowrap">
                            Ranked
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs sm:text-sm mb-3">
                      {list.totalItems} items
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleEditList(list)}
                        className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteList(list.id)}
                        className="text-red-400 hover:text-red-300 text-xs sm:text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )
              )}
              {isSearchingLists && searchedListsForManagement.length === 0 && (
                <p className="text-gray-400 text-center py-8 text-sm">
                  No lists match your search.
                </p>
              )}
              {!isSearchingLists && lists.length === 0 && (
                <p className="text-gray-400 text-center py-8 text-sm">
                  No lists found.
                </p>
              )}
            </div>

            {!isSearchingLists && totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4 pt-4 border-t border-gray-700">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(0, prev - 1))
                  }
                  disabled={currentPage === 0}
                  className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-dark-bg border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                >
                  Prev
                </button>
                <span className="text-gray-400 text-xs sm:text-sm">
                  {currentPage + 1} / {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
                  }
                  disabled={currentPage === totalPages - 1}
                  className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-dark-bg border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "categories" && (
        <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-8">
          <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              {editingCategory ? "Edit Category" : "Create New Category"}
            </h2>
            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                  Name *
                </label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, name: e.target.value })
                  }
                  required
                  className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-gray-300 text-sm sm:text-base">
                    Description
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowFormattingHelp(!showFormattingHelp)}
                    className="text-blue-400 hover:text-blue-300 text-xs"
                  >
                    {showFormattingHelp ? "Hide" : "Help"}
                  </button>
                </div>

                {showFormattingHelp && (
                  <div className="bg-dark-bg border border-gray-600 rounded p-3 mb-2 text-xs text-gray-300 space-y-1">
                    <p>
                      <code className="bg-gray-700 px-1 rounded">**bold**</code>{" "}
                      = <strong>bold</strong>
                    </p>
                    <p>
                      <code className="bg-gray-700 px-1 rounded">*italic*</code>{" "}
                      = <em>italic</em>
                    </p>
                    <p>
                      <code className="bg-gray-700 px-1 rounded">- item</code> =
                      Bullet point
                    </p>
                  </div>
                )}

                <textarea
                  value={categoryForm.description}
                  onChange={(e) =>
                    setCategoryForm({
                      ...categoryForm,
                      description: e.target.value,
                    })
                  }
                  rows="4"
                  placeholder="Supports **bold**, *italic*, lists"
                  className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition text-sm sm:text-base"
                >
                  {editingCategory ? "Update" : "Create"}
                </button>
                {editingCategory && (
                  <button
                    type="button"
                    onClick={handleCancelEditCategory}
                    className="px-4 sm:px-6 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg transition text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              All Categories ({categories.length})
            </h2>
            <div className="space-y-3 max-h-[400px] sm:max-h-[600px] overflow-y-auto">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="bg-dark-bg border border-gray-600 p-3 sm:p-4 rounded-lg flex justify-between items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-sm sm:text-base truncate">
                      {category.name}
                    </h3>
                    <p className="text-gray-400 text-xs sm:text-sm">
                      {category.listCount}{" "}
                      {category.listCount === 1 ? "list" : "lists"}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleEditCategory(category)}
                      className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-red-400 hover:text-red-300 text-xs sm:text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "items" && (
        <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-8">
          <div className="space-y-6">
            <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                Select a List
              </h2>
              <input
                type="text"
                placeholder="Search lists by name..."
                value={listSearchQuery}
                onChange={(e) => setListSearchQuery(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm sm:text-base focus:outline-none focus:border-blue-500 mb-3"
              />
              <div className="max-h-64 overflow-y-auto bg-dark-bg border border-gray-600 rounded-lg">
                {!listSearchQuery.trim() ? (
                  <p className="text-gray-400 text-center py-8 text-sm">
                    Start typing to search for lists...
                  </p>
                ) : searchedLists.length === 0 ? (
                  <p className="text-gray-400 text-center py-8 text-sm">
                    No lists match your search.
                  </p>
                ) : (
                  searchedLists.map((list) => (
                    <button
                      key={list.id}
                      onClick={() => {
                        handleListSelect(list.id);
                        setItemPage(0);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-700 transition border-b border-gray-700 last:border-b-0 ${
                        selectedList === list.id ? "bg-blue-600/20" : ""
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm font-medium truncate">
                          {list.title}
                        </span>
                        <span className="text-gray-400 text-xs ml-2 flex-shrink-0">
                          {list.totalItems} items
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {selectedList && (
              <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                  {editingItem ? "Edit Item" : "Add Item"}
                </h2>
                <form
                  onSubmit={editingItem ? handleUpdateItem : handleAddItem}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                      Answer *
                    </label>
                    <input
                      type="text"
                      value={itemForm.answer}
                      onChange={(e) =>
                        setItemForm({ ...itemForm, answer: e.target.value })
                      }
                      required
                      className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                      Rank Position (optional)
                    </label>
                    <input
                      type="number"
                      value={itemForm.rankPosition || ""}
                      onChange={(e) =>
                        setItemForm({
                          ...itemForm,
                          rankPosition: e.target.value
                            ? parseInt(e.target.value)
                            : null,
                        })
                      }
                      className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition text-sm sm:text-base"
                    >
                      {editingItem ? "Update" : "Add"}
                    </button>
                    {editingItem && (
                      <button
                        type="button"
                        onClick={handleCancelEditItem}
                        className="px-4 sm:px-6 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg transition text-sm sm:text-base"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>

          {selectedList &&
            (() => {
              const filteredItems = getFilteredItems();
              const itemsPerPage = 100;
              const totalItemPages = Math.ceil(
                filteredItems.length / itemsPerPage
              );
              const paginatedItems = filteredItems.slice(
                itemPage * itemsPerPage,
                (itemPage + 1) * itemsPerPage
              );

              return (
                <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
                  <div className="mb-4">
                    <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                      Items ({listItems.length})
                    </h2>
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={itemSearchQuery}
                      onChange={(e) => {
                        setItemSearchQuery(e.target.value);
                        setItemPage(0);
                      }}
                      className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm sm:text-base focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-2 max-h-[400px] sm:max-h-[600px] overflow-y-auto">
                    {paginatedItems.length === 0 ? (
                      <p className="text-gray-400 text-center py-8 text-sm sm:text-base">
                        {itemSearchQuery
                          ? "No items match your search."
                          : "No items yet. Add some above!"}
                      </p>
                    ) : (
                      paginatedItems.map((item) => (
                        <div
                          key={item.id}
                          className="bg-dark-bg border border-gray-600 p-3 rounded-lg flex justify-between items-center gap-3"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                            {item.rankPosition && (
                              <span className="text-gray-400 font-mono text-xs sm:text-sm flex-shrink-0">
                                #{item.rankPosition}
                              </span>
                            )}
                            <span className="text-white text-sm sm:text-base truncate">
                              {item.answer}
                            </span>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleEditItem(item)}
                              className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              className="text-red-400 hover:text-red-300 text-xs sm:text-sm font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {totalItemPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-4 pt-4 border-t border-gray-700 flex-wrap">
                      <button
                        onClick={() => setItemPage(Math.max(0, itemPage - 1))}
                        disabled={itemPage === 0}
                        className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-dark-bg border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                      >
                        Prev
                      </button>

                      <button
                        onClick={() => setItemPage(0)}
                        className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded ${
                          itemPage === 0
                            ? "bg-blue-600 text-white"
                            : "bg-dark-bg border border-gray-600 text-white hover:bg-gray-700"
                        }`}
                      >
                        1
                      </button>

                      {itemPage > 2 && (
                        <span className="text-gray-400 px-2">...</span>
                      )}

                      {Array.from({ length: totalItemPages }, (_, i) => i)
                        .filter(
                          (i) =>
                            i > 0 &&
                            i < totalItemPages - 1 &&
                            Math.abs(i - itemPage) <= 1
                        )
                        .map((i) => (
                          <button
                            key={i}
                            onClick={() => setItemPage(i)}
                            className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded ${
                              itemPage === i
                                ? "bg-blue-600 text-white"
                                : "bg-dark-bg border border-gray-600 text-white hover:bg-gray-700"
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}

                      {itemPage < totalItemPages - 3 && (
                        <span className="text-gray-400 px-2">...</span>
                      )}

                      {totalItemPages > 1 && (
                        <button
                          onClick={() => setItemPage(totalItemPages - 1)}
                          className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded ${
                            itemPage === totalItemPages - 1
                              ? "bg-blue-600 text-white"
                              : "bg-dark-bg border border-gray-600 text-white hover:bg-gray-700"
                          }`}
                        >
                          {totalItemPages}
                        </button>
                      )}

                      <button
                        onClick={() =>
                          setItemPage(
                            Math.min(totalItemPages - 1, itemPage + 1)
                          )
                        }
                        disabled={itemPage === totalItemPages - 1}
                        className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-dark-bg border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
        </div>
      )}

      {activeTab === "users" && (
        <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              All Users ({users.length})
            </h2>
            <input
              type="text"
              placeholder="Search users..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm sm:text-base focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Mobile View - Cards */}
          <div className="sm:hidden space-y-3">
            {getFilteredUsers().length === 0 ? (
              <p className="text-gray-400 text-center py-8 text-sm">
                {userSearchQuery
                  ? "No users match your search."
                  : "No users found."}
              </p>
            ) : (
              getFilteredUsers().map((user) => (
                <div
                  key={user.id}
                  className="bg-dark-bg border border-gray-600 p-4 rounded-lg space-y-2"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-white font-semibold">
                        {user.username}
                      </h3>
                      <p className="text-gray-400 text-sm">{user.email}</p>
                    </div>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        user.isAdmin
                          ? "bg-yellow-600/20 text-yellow-400"
                          : "bg-blue-600/20 text-blue-400"
                      }`}
                    >
                      {user.isAdmin ? "Admin" : "User"}
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs">
                    Joined: {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    disabled={user.isAdmin}
                    className="w-full bg-red-600/20 border border-red-600 text-red-400 hover:bg-red-600/30 py-2 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {user.isAdmin ? "Cannot Delete Admin" : "Delete User"}
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Desktop View - Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 font-semibold pb-3 px-4 text-sm">
                    Username
                  </th>
                  <th className="text-left text-gray-400 font-semibold pb-3 px-4 text-sm">
                    Email
                  </th>
                  <th className="text-left text-gray-400 font-semibold pb-3 px-4 text-sm">
                    Role
                  </th>
                  <th className="text-left text-gray-400 font-semibold pb-3 px-4 text-sm">
                    Joined
                  </th>
                  <th className="text-right text-gray-400 font-semibold pb-3 px-4 text-sm">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {getFilteredUsers().length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="py-8 text-center text-gray-400 text-sm"
                    >
                      {userSearchQuery
                        ? "No users match your search."
                        : "No users found."}
                    </td>
                  </tr>
                ) : (
                  getFilteredUsers().map((user) => (
                    <tr
                      key={user.id}
                      className="border-b border-gray-700 hover:bg-dark-bg"
                    >
                      <td className="py-4 px-4 text-white text-sm">
                        {user.username}
                      </td>
                      <td className="py-4 px-4 text-gray-400 text-sm">
                        {user.email}
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            user.isAdmin
                              ? "bg-yellow-600/20 text-yellow-400"
                              : "bg-blue-600/20 text-blue-400"
                          }`}
                        >
                          {user.isAdmin ? "Admin" : "User"}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-gray-400 text-sm">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={user.isAdmin}
                          className="text-red-400 hover:text-red-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            user.isAdmin
                              ? "Cannot delete admin users"
                              : "Delete user"
                          }
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
